import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState, useEffect, useMemo, useRef, Component, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTracking } from "@/hooks/use-tracking";
import { useAuth } from "@/hooks/use-auth";
import { checkScrapingLimit } from "@/lib/anti-scraping";
import { useHeaderHeight } from "@/hooks/use-header-height";
import { 
  Loader2, 
  Search, 
  CheckCircle2, 
  Heart, 
  Building2, 
  MapPin, 
  Filter, 
  X, 
  BadgeCheck, 
  Lock,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  AlertCircle,
  Copy,
  RefreshCw
} from "lucide-react";
import { FornecedorCardSkeleton } from "@/components/ui/skeletons";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

class ErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode | ((error: Error | null, info: any) => ReactNode) }, { hasError: boolean; error: Error | null; errorInfo: any }> {
  constructor(props: { children: ReactNode; fallback: ReactNode | ((error: Error | null, info: any) => ReactNode) }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  override componentDidCatch(error: Error, errorInfo: any) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }
  override render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return (this.props.fallback as any)(this.state.error, this.state.errorInfo);
      }
      return this.props.fallback as ReactNode;
    }
    return this.props.children;
  }
}
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { getIcone } from "@/lib/icones";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import debounce from "lodash.debounce";
import { POLOS } from "@/constants/polos";

const ITEMS_PER_PAGE = 24;


import { z } from "zod";

export const fornecedoresSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  page: z.coerce.number().int().positive().optional().catch(undefined),
  order: z.enum(["default", "recent", "popular"]).optional().catch(undefined),
  segmentos: z.string().optional().catch(undefined),
  uf: z.string().optional().catch(undefined),
  cidade: z.string().optional().catch(undefined),
  polos: z.array(z.string()).optional().catch(undefined),
  verificado: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional().catch(undefined),
  varejo: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional().catch(undefined),
}).passthrough();

export type FornecedoresSearch = z.infer<typeof fornecedoresSearchSchema>;

export const Route = createFileRoute("/_authenticated/fornecedores/")({
  validateSearch: (search: Record<string, unknown>) => fornecedoresSearchSchema.parse(search),
  component: FornecedoresList,
});








function FornecedoresList() {
  const search: FornecedoresSearch = Route.useSearch();
  const { q, page, order, segmentos: segmentosParam, uf, cidade, polos, verificado, varejo } = search;
  const { user } = useAuth();
  const headerHeight = useHeaderHeight();
  const navigate = useNavigate({ from: Route.fullPath });
  const { registrar } = useTracking();
  const linkPrefetchRef = useRef<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [warning, setWarning] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [favoritosIds, setFavoritosIds] = useState<Set<string>>(new Set());

  // Carregar favoritos do usuário para mostrar o estado do coração
  useEffect(() => {
    async function loadFavoritos() {
      if (!user) return;
      const { data } = await supabase
        .from('favoritos')
        .select('fornecedor_id')
        .eq('user_id', user.id);
      
      if (data) {
        setFavoritosIds(new Set(data.map(f => f.fornecedor_id)));
      }
    }
    loadFavoritos();
  }, [user]);

  const toggleFavorite = async (e: React.MouseEvent, fornecedorId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) return;

    const isFav = favoritosIds.has(fornecedorId);
    
    // Atualização otimista local
    const newIds = new Set(favoritosIds);
    if (isFav) newIds.delete(fornecedorId);
    else newIds.add(fornecedorId);
    setFavoritosIds(newIds);

    try {
      if (isFav) {
        const { error, count } = await supabase
          .from('favoritos')
          .delete({ count: 'exact' })
          .eq('fornecedor_id', fornecedorId)
          .eq('user_id', user.id);

        
        if (error) throw error;
        if (count === 0) {
          toast.warning("Não foi possível remover: registro não encontrado ou acesso negado.");
          // Reverte
          const revertIds = new Set(favoritosIds);
          revertIds.add(fornecedorId);
          setFavoritosIds(revertIds);
          return;
        }
        toast.success("Removido dos Favoritos", {
          action: { label: "Ver Favoritos", onClick: () => navigate({ to: '/favoritos' }) }
        });
      } else {
        const { data, error } = await supabase
          .from('favoritos')
          .insert({ fornecedor_id: fornecedorId, user_id: user.id })
          .select();
        
        if (error) throw error;
        if (!data || data.length === 0) {
          toast.warning("Não foi possível salvar: verifique seu status de aprovação.");
          // Reverte
          const revertIds = new Set(favoritosIds);
          revertIds.delete(fornecedorId);
          setFavoritosIds(revertIds);
          return;
        }
        toast.success("Salvo em Favoritos", {
          action: { label: "Ver Favoritos", onClick: () => navigate({ to: '/favoritos' }) }
        });
      }
      queryClient.invalidateQueries({ queryKey: ['favoritos'] });
      queryClient.invalidateQueries({ queryKey: ['is-favorite', fornecedorId] });
    } catch (err: any) {
      console.error("Error toggling favorite:", err);
      // Reverter em caso de erro
      setFavoritosIds(favoritosIds);
      toast.error("Erro ao atualizar favoritos: " + (err.message || "Erro desconhecido"));
    }
  };


  // Cache para evitar requisições duplicadas ao navegar entre rotas
  const cache = useRef<Record<string, { data: any[], count: number, timestamp: number }>>({});
  const CACHE_TTL = 1000 * 60 * 5; // 5 minutos


  // Immediate input state for UX
  const [searchInput, setSearchInput] = useState(q || "");

  // Metadata
  const { data: segmentosData = [], isLoading: isLoadingSegmentos } = useQuery({
    queryKey: ['segmentos-active'],
    queryFn: async () => {
      const { data } = await supabase.from('segmentos').select('*').eq('ativo', true).order('ordem');
      return data || [];
    },
    staleTime: 1000 * 60 * 30,
  });

  const segmentos = useMemo(() => segmentosData || [], [segmentosData]);

  // Resolve slugs to IDs and sync URL
  const resolvedSegmentIds = useMemo(() => {
    if (!segmentosParam || !segmentos.length) return [];
    
    const slugs = segmentosParam.split(',').map((s: string) => s.trim()).filter(Boolean);
    const resolvedIds = slugs
      .map((slug: string) => segmentos.find((s: any) => s.slug === slug)?.id)
      .filter((id: any): id is string => !!id);
    
    return resolvedIds;
  }, [segmentosParam, segmentos]);

  // Handle segment warnings separately to avoid re-render loops in useMemo
  useEffect(() => {
    if (!segmentosParam || !segmentos.length || isLoadingSegmentos) return;
    
    const slugs = segmentosParam.split(',').map((s: string) => s.trim()).filter(Boolean);
    const resolvedIds = slugs
      .map((slug: string) => segmentos.find((s: any) => s.slug === slug)?.id)
      .filter((id: any): id is string => !!id);
      
    if (slugs.length > 0 && resolvedIds.length === 0) {
      setWarning("Segmento não encontrado. Exibindo todos os fornecedores.");
    } else {
      setWarning(null);
    }
  }, [segmentosParam, segmentos, isLoadingSegmentos]);

  const { data: segmentoCounts = {} } = useQuery({
    queryKey: ['segment-counts'],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_segment_counts' as any);
      return (data as any[] || []).reduce((acc, curr) => ({ ...acc, [curr.segmento_id]: curr.count }), {});
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: poloCounts = {} } = useQuery({
    queryKey: ['polo-counts'],
    queryFn: async () => {
      const { data } = await supabase.from('fornecedores').select('polo').eq('status', 'aprovado').filter('suspeito', 'is', false).not('polo', 'is', null);
      return (data || []).reduce((acc: any, curr: any) => {
        acc[curr.polo] = (acc[curr.polo] || 0) + 1;
        return acc;
      }, {});
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: ufs = [] } = useQuery({
    queryKey: ['ufs-list'],
    queryFn: async () => {
      const { data } = await supabase.from('fornecedores').select('uf').eq('status', 'aprovado').filter('suspeito', 'is', false).not('uf', 'is', null);
      return [...new Set(data?.map(l => l.uf).filter(Boolean) as string[])].sort();
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: cidades = [] } = useQuery({
    queryKey: ['cidades-by-uf', uf],
    queryFn: async () => {
      if (!uf || uf === "all") return [];
      const { data } = await supabase.from('fornecedores').select('cidade').eq('status', 'aprovado').filter('suspeito', 'is', false).eq('uf', uf || '');
      return [...new Set(data?.map(l => l.cidade).filter(Boolean) as string[])].sort();
    },
    enabled: !!uf && uf !== "all",
    staleTime: 1000 * 60 * 30,
  });

  const debouncedSearch = useMemo(
    () => debounce((val: string) => {
      navigate({ search: (prev) => ({ ...prev, q: val, page: 1 }) });
    }, 500),
    [navigate]
  );

  useEffect(() => {
    setSearchInput(q || "");
  }, [q]);

  useEffect(() => {
    if (isLoadingSegmentos) return;
    fetchFornecedores();
  }, [page, q, order, segmentosParam, uf, cidade, polos, verificado, varejo, isLoadingSegmentos]);

  const fetchFornecedores = useCallback(async () => {
    // Ensure all search params are primitives for cache key
    const cacheKey = JSON.stringify({ 
      q: q || "", 
      page: page || 1, 
      order: order || "default", 
      segmentos: segmentosParam || "", 
      uf: uf || "", 
      cidade: cidade || "", 
      polos: polos || [], 
      verificado: !!verificado, 
      varejo: !!varejo 
    });
    const cached = cache.current[cacheKey];
    
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
      setFornecedores(cached.data);
      setTotalCount(cached.count);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('fornecedores')
        .select('id, nome, cidade, uf, polo, verificado, destaque, logo_url, segmentos(nome, icone)', { count: 'estimated' })
        .eq('status', 'aprovado')
        .eq('suspeito', false);

      if (typeof q === 'string' && q.trim()) {
        query = query.or(`nome.ilike.%${q}%,razao_social.ilike.%${q}%,cidade.ilike.%${q}%`);
      }

      if (resolvedSegmentIds && resolvedSegmentIds.length > 0) {
        // Validate each ID is a valid UUID to prevent Postgres errors
        const validIds = resolvedSegmentIds.filter(id => 
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
        );
        if (validIds.length > 0) {
          query = query.in('segmento_id', validIds);
        }
      }

      if (typeof uf === 'string' && uf !== "all") query = query.eq('uf', uf);
      if (typeof cidade === 'string' && cidade !== "all") query = query.eq('cidade', cidade);
      if (Array.isArray(polos) && polos.length > 0) query = query.in('polo', polos);
      if (verificado) query = query.eq('verificado', true);
      if (varejo) query = query.eq('atende_varejo', true);

      // Sorting - with safe fallback for unknown order values
      const sortOrder = ["default", "recent", "popular"].includes(order || "") ? order : "default";

      if (sortOrder === "default") {
        query = query
          .order('destaque', { ascending: false })
          .order('verificado', { ascending: false })
          .order('cidade', { ascending: false, nullsFirst: false })
          .order('telefone', { ascending: false, nullsFirst: false })
          .order('nome', { ascending: true });
      } else if (sortOrder === "recent") {
        query = query.order('created_at', { ascending: false });
      } else if (sortOrder === "popular") {
        query = query.order('verificado', { ascending: false }).order('destaque', { ascending: false });
      }

      const from = ((page || 1) - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data, count, error: fetchError } = await query.range(from, to);

      if (fetchError) throw fetchError;

      if (data) {
        setFornecedores(data);
        const countVal = count !== null ? count : 0;
        setTotalCount(countVal);
        
        // Update Cache
        cache.current[cacheKey] = {
          data,
          count: countVal,
          timestamp: Date.now()
        };

        if (q) registrar('busca', { termo: q, resultados: countVal });
      }
    } catch (err: any) {
      console.error("Error fetching suppliers:", err);
      setError("Ocorreu um erro ao carregar os fornecedores. Por favor, tente novamente.");
      toast.error("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  }, [q, page, order, segmentosParam, uf, cidade, polos, verificado, varejo, user, registrar, resolvedSegmentIds]);

  const handleVerContato = async (id: string) => {
    if (!user) return;
    const limitReached = await checkScrapingLimit(user.id);
    if (limitReached) {
      toast.error("Você atingiu o limite diário de consultas. Ele é reiniciado em 24h.");
      return;
    }
    navigate({ to: '/fornecedores/$id', params: { id } });
  };



  const clearFilters = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        q: undefined,
        page: 1,
        order: "default",
        segmentos: undefined,
        uf: undefined,
        cidade: undefined,
        polos: undefined,
        verificado: undefined,
        varejo: undefined,
      })
    });
  };


  const FiltersContent = () => (
    <div className="space-y-6">
      <Accordion type="multiple" defaultValue={["segmentos", "localizacao", "polo", "opcoes"]} className="w-full">
        <AccordionItem value="segmentos" className="border-none">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="font-bold text-[#0C1A12]">Segmentos</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200">
              {(segmentos ?? []).map((s) => (
                <div key={s.id} className="flex items-center justify-between group cursor-pointer">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                       id={s.id} 
                       checked={resolvedSegmentIds?.includes(s.id) || false}
                       onCheckedChange={(checked) => {
                         const newIds = checked 
                           ? [...(resolvedSegmentIds || []), s.id] 
                           : (resolvedSegmentIds || []).filter((id: string) => id !== s.id);
                         
                         // Convert IDs back to slugs for the URL
                         const newSlugs = newIds
                           .map(id => (segmentos ?? []).find(seg => seg.id === id)?.slug)
                           .filter(Boolean)
                           .join(',');
 
                          navigate({ search: (prev) => ({ ...prev, segmentos: newSlugs || undefined, page: 1 }) });
                       }}
 
                      className="rounded-[4px] border-gray-300 data-[state=checked]:bg-[#C6F764] data-[state=checked]:border-[#C6F764] data-[state=checked]:text-[#0C1A12]"
                    />
                    <label htmlFor={s.id} className="text-sm font-medium leading-none cursor-pointer group-hover:text-primary transition-colors flex items-center gap-2">
                      {(() => {
                        const Icon = getIcone(s.icone);
                        return <Icon className="w-3.5 h-3.5 opacity-60" />;
                      })()}
                      {s.nome}
                    </label>
                  </div>
                  <span className="text-[10px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full font-bold">
                    {segmentoCounts[s.id] || 0}
                  </span>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="localizacao" className="border-none">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="font-bold text-[#0C1A12]">Localização</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Estado (UF)</Label>
                <Select value={uf || "all"} onValueChange={(val) => navigate({ search: (prev) => ({ ...prev, uf: val === "all" ? undefined : val, cidade: undefined, page: 1 }) })}>
                  <SelectTrigger className="rounded-xl bg-secondary/50 border-transparent"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {ufs.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Cidade</Label>
                <Select value={cidade || "all"} onValueChange={(val) => navigate({ search: (prev) => ({ ...prev, cidade: val === "all" ? undefined : val, page: 1 }) })} disabled={!uf || uf === "all"}>
                  <SelectTrigger className="rounded-xl bg-secondary/50 border-transparent"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {cidades.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="polo" className="border-none">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="font-bold text-[#0C1A12]">Polo Atacadista</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              {POLOS.filter(p => (poloCounts[p.nome] || 0) > 0).map((p) => (
                <div key={p.nome} className="flex items-center justify-between group cursor-pointer">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id={`polo-${p.nome}`} 
                      checked={polos?.includes(p.nome) || false}
                      onCheckedChange={(checked) => {
                        const newPolos = checked
                          ? [...(polos || []), p.nome]
                          : (polos || []).filter((item: string) => item !== p.nome);
                        navigate({ search: (prev: FornecedoresSearch) => ({ ...prev, polos: newPolos.length > 0 ? newPolos : undefined, page: 1 }) });
                      }}

                      className="rounded-[4px] border-gray-300 data-[state=checked]:bg-[#C6F764] data-[state=checked]:border-[#C6F764] data-[state=checked]:text-[#0C1A12]"
                    />
                    <label htmlFor={`polo-${p.nome}`} className="text-sm font-medium leading-none cursor-pointer group-hover:text-primary transition-colors flex items-center gap-1">
                      {p.nome}
                      {p.uf && <span className="text-[10px] text-muted-foreground uppercase">{p.uf}</span>}
                    </label>
                  </div>
                  <span className="text-[10px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full font-bold">
                    {poloCounts[p.nome]}
                  </span>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="opcoes" className="border-none">
          <AccordionTrigger className="hover:no-underline py-3">
            <span className="font-bold text-[#0C1A12]">Opções</span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              <div className="flex items-center space-x-2 group">
                <Checkbox 
                  id="verificado" 
                  checked={verificado || false}
                  onCheckedChange={(checked) => navigate({ search: (prev: FornecedoresSearch) => ({ ...prev, verificado: checked ? true : undefined, page: 1 }) })}
                  className="rounded-[4px] border-gray-300 data-[state=checked]:bg-[#C6F764] data-[state=checked]:border-[#C6F764] data-[state=checked]:text-[#0C1A12]"
                />
                <label htmlFor="verificado" className="text-sm font-medium leading-none cursor-pointer group-hover:text-primary transition-colors">Somente verificados</label>
              </div>
              <div className="flex items-center space-x-2 group">
                <Checkbox 
                  id="varejo" 
                  checked={varejo || false}
                  onCheckedChange={(checked) => navigate({ search: (prev: FornecedoresSearch) => ({ ...prev, varejo: checked ? true : undefined, page: 1 }) })}
                  className="rounded-[4px] border-gray-300 data-[state=checked]:bg-[#C6F764] data-[state=checked]:border-[#C6F764] data-[state=checked]:text-[#0C1A12]"
                />
                <label htmlFor="varejo" className="text-sm font-medium leading-none cursor-pointer group-hover:text-primary transition-colors">Atende varejo</label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Button variant="outlineLight" className="w-full rounded-2xl h-12 border-2 border-gray-200 font-bold" onClick={clearFilters}>Limpar tudo</Button>
    </div>
  );


  return (
      <div className="bg-secondary/30">
        <div 
          className="bg-[#C6F764] py-4 md:py-4 sticky z-40 shadow-[0_1px_3px_rgba(12,26,18,0.08)]"
          style={{ top: headerHeight }}
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative w-full md:max-w-xl group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280] group-focus-within:text-[#0C1A12] transition-colors" />
                <Input 
                  placeholder="Buscar por nome, razão social ou cidade..." 
                  className="pl-12 h-12 rounded-[10px] bg-white border-none shadow-sm focus-visible:ring-2 focus-visible:ring-[#0C1A12] focus-visible:ring-offset-0 placeholder:text-[#6B7280] text-[#0C1A12]"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                />
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <Select value={order || "default"} onValueChange={(val) => navigate({ search: (prev) => ({ ...prev, order: val as "default" | "popular" | "recent", page: 1 }) })}>
                  <SelectTrigger className="w-full md:w-48 rounded-[10px] h-12 bg-white border-none shadow-sm text-[#0C1A12] focus:ring-2 focus:ring-[#0C1A12] focus:ring-offset-0">
                    <div className="flex items-center gap-2"><ArrowUpDown className="w-4 h-4" /><SelectValue /></div>
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="default">Relevância</SelectItem>
                    <SelectItem value="popular">Mais acessados</SelectItem>
                    <SelectItem value="recent">Mais recentes</SelectItem>
                  </SelectContent>
                </Select>


                <div className="md:hidden w-full">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="w-full h-12 rounded-xl gap-2">
                        <Filter className="w-4 h-4" /> Filtros
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                      <SheetHeader className="mb-8">
                        <SheetTitle>Filtros</SheetTitle>
                      </SheetHeader>
                      <FiltersContent />
                    </SheetContent>
                  </Sheet>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters Desktop */}
            <aside className="hidden lg:block w-72 shrink-0">
              <div 
                className="bg-white rounded-3xl p-8 border shadow-sm sticky"
                style={{ top: headerHeight + 80 }}
              >
                <h3 className="text-lg font-bold mb-6 flex items-center gap-2"><Filter className="w-5 h-5" /> Filtros</h3>
                <FiltersContent />
              </div>
            </aside>

            {/* List */}
            <div className="flex-1">
              {warning && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <p className="text-sm font-medium">{warning}</p>
                </div>
              )}

              {loading ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <FornecedorCardSkeleton key={i} />
                  ))}
                </div>
              ) : error ? (
                <div className="bg-white rounded-3xl p-16 text-center border shadow-sm">
                  <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                    <AlertCircle className="w-10 h-10" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-brand-dark">Ops! Algo deu errado</h2>
                  <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                    {error}
                  </p>
                  <Button onClick={() => fetchFornecedores()} variant="outlineLight" className="rounded-xl px-8 border-brand-dark">Tentar novamente</Button>
                </div>
              ) : (
                <ErrorBoundary
                  fallback={(error: Error | null, info: any) => (
                    <div className="bg-white rounded-3xl p-12 text-center border shadow-sm max-w-2xl mx-auto my-8">
                      <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                        <AlertCircle className="w-10 h-10" />
                      </div>
                      <h2 className="text-2xl font-bold mb-3 text-brand-dark">Ops! Algo quebrou na listagem</h2>
                      <p className="text-muted-foreground mb-6">
                        {error?.message || "Ocorreu um erro inesperado ao renderizar os fornecedores."}
                      </p>
                      
                      {error && (
                        <div className="mb-8 p-4 bg-secondary/30 rounded-xl text-left overflow-hidden">
                          <p className="text-xs font-mono text-muted-foreground break-all line-clamp-4 mb-3">
                            {error.stack}
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-[10px] gap-1.5 rounded-lg"
                              onClick={() => {
                                navigator.clipboard.writeText(`Error: ${error.message}\n\nStack: ${error.stack}\n\nComponent Stack: ${info?.componentStack}`);
                                toast.success("Erro copiado para a área de transferência");
                              }}
                            >
                              <Copy className="w-3 h-3" /> Copiar erro completo
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button onClick={() => window.location.reload()} variant="onLight" className="rounded-xl px-8 bg-[#0C1A12] text-white gap-2">
                          <RefreshCw className="w-4 h-4" /> Recarregar página
                        </Button>
                        <Button onClick={() => fetchFornecedores()} variant="outlineLight" className="rounded-xl px-8 border-brand-dark">
                          Tentar novamente
                        </Button>
                      </div>
                    </div>
                  )}
                >
                  {fornecedores.length === 0 ? (
                    <div className="bg-white rounded-3xl p-16 text-center border shadow-sm">
                      <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6 text-muted-foreground">
                        <Search className="w-10 h-10" />
                      </div>
                      <h2 className="text-2xl font-bold mb-3 text-brand-dark">Nenhum resultado encontrado</h2>
                      <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                        Não encontramos fornecedores com esses critérios. Tente limpar os filtros ou buscar por outro termo.
                      </p>
                      <Button onClick={clearFilters} variant="outlineLight" className="rounded-xl px-8 border-brand-dark">Limpar todos os filtros</Button>
                    </div>
                  ) : (
                    <>
                  <div className="flex flex-col gap-4 mb-6">
                    <div className="flex flex-wrap gap-2">
                      {resolvedSegmentIds.map(id => {
                        const s = segmentos.find(seg => seg.id === id);
                        if (!s) return null;
                        return (
                          <Badge key={id} variant="secondary" className="h-7 px-3 rounded-full flex items-center gap-1.5 bg-[#C6F764] text-[#0C1A12] border-none">
                            {s.nome}
                            <button 
                              onClick={() => {
                                const newIds = resolvedSegmentIds.filter(i => i !== id);
                                const newSlugs = newIds
                                  .map(i => (segmentos ?? []).find(seg => seg.id === i)?.slug)
                                  .filter(Boolean)
                                  .join(',');
                                navigate({ search: (prev: FornecedoresSearch) => ({ ...prev, segmentos: newSlugs || undefined, page: 1 }) });
                              }}
                              className="hover:text-black/60 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">{totalCount} fornecedores encontrados</p>
                      <div className="flex items-center gap-2">
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                      </div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                    {fornecedores.map((f) => (
                      <Card 
                        key={f.id} 
                        className={`bg-white p-6 border border-[#E5E7EB] rounded-[16px] shadow-sm hover:shadow-xl transition-all relative group overflow-hidden cursor-pointer ${f.destaque ? 'ring-2 ring-[#C6F764] shadow-md' : ''}`}
                        onClick={() => navigate({ to: '/fornecedores/$id', params: { id: f.id } })}
                      >

                        {f.destaque && (
                          <div className="absolute top-0 right-0 z-10">
                            <div className="bg-[#C6F764] text-[#0C1A12] text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">Destaque</div>
                          </div>
                        )}

                        
                        <div className="flex justify-between items-start mb-4">
                          <div className="w-14 h-14 bg-secondary/50 rounded-2xl flex items-center justify-center overflow-hidden border border-gray-100">
                            {f.logo_url ? (
                              <img src={f.logo_url} alt={f.nome} className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-bold text-xl text-gray-400">{f.nome[0]}</span>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className={`rounded-full hover:bg-red-50 transition-colors ${favoritosIds.has(f.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                            onClick={(e) => toggleFavorite(e, f.id)}
                          >
                            <Heart className={`w-5 h-5 ${favoritosIds.has(f.id) ? 'fill-current' : ''}`} />
                          </Button>

                        </div>

                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-bold group-hover:text-[#0C1A12] transition-colors line-clamp-1 leading-tight">{f.nome}</h3>
                            {f.verificado && <BadgeCheck className="w-5 h-5 text-[#C6F764] shrink-0" />}
                          </div>
                          
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="bg-secondary/30 border-none text-[10px] font-bold uppercase tracking-wider py-1 px-2.5 rounded-lg flex items-center gap-1.5">
                              {(() => {
                                const Icon = getIcone(f.segmentos?.icone);
                                return <Icon className="w-3 h-3" />;
                              })()}
                              {f.segmentos?.nome}
                            </Badge>
                            {f.cidade && f.uf && (
                              <Badge variant="outline" className="bg-secondary/30 border-none text-[10px] font-bold uppercase tracking-wider py-1 px-2.5 rounded-lg flex items-center gap-1.5">
                                <MapPin className="w-3 h-3" />
                                {f.cidade}/{f.uf}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <Button 
                          variant="onLight"
                          className="w-full rounded-xl font-bold h-12 bg-[#0C1A12] text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVerContato(f.id);
                          }}

                        >
                          Ver contato
                        </Button>

                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalCount > ITEMS_PER_PAGE && (
                    <div className="flex items-center justify-center gap-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="rounded-xl" 
                        disabled={Number(page || 1) <= 1}
                        onClick={() => {
                          navigate({ search: (prev: FornecedoresSearch) => ({ ...prev, page: Math.max(1, Number(page || 1) - 1) }) });
                          window.scrollTo(0, 0);
                        }}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <span className="text-sm font-bold mx-4">Página {Number(page || 1)} de {Math.ceil(totalCount / ITEMS_PER_PAGE)}</span>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="rounded-xl"
                        disabled={Number(page || 1) * ITEMS_PER_PAGE >= totalCount}
                        onClick={() => {
                          navigate({ search: (prev: FornecedoresSearch) => ({ ...prev, page: Number(page || 1) + 1 }) });
                          window.scrollTo(0, 0);
                        }}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  )}
                </>
              )}
              </ErrorBoundary>
            )}
            </div>
          </div>
        </div>
      </div>
  );
}

